// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// Shared helpers for every whiteboard transport (1:1 data channel WB-003, mesh
// WB-004, SFU WS fallback WB-005). Keeps the anti-spoof author-stamping and the
// op-id idempotency guard in one place so the three pipes can't drift.

import type { WireOp } from "./types";

/**
 * The negotiated `RTCDataChannel` both peers open with the SAME id, so the
 * channel comes up as the call connects with no extra offer/answer round-trip.
 */
export const WHITEBOARD_DC_LABEL = "whiteboard";
export const WHITEBOARD_DC_ID = 1;

/**
 * Re-stamp the author of an op received over a transport with the authenticated
 * peer identity — NEVER trust the payload's `author` (anti-spoof, mirrors the
 * `handleSignal` rule). A live op (stroke / erase / clear / control) is authored
 * by the peer that sent it; a `batch` carries only that peer's ops so its inner
 * ops are stamped too.
 *
 * A `snapshot` is the exception: it replays MANY authors' historical ops, so its
 * inner ops keep their original authorship (only the envelope is the peer's).
 * Late-join sync (WB-006) relies on this — overwriting would erase attribution.
 */
export function stampAuthor(op: WireOp, author: string): WireOp {
  switch (op.op) {
    case "batch":
      return {
        ...op,
        author,
        ops: op.ops.map((inner) => ({ ...inner, author })),
      };
    case "snapshot":
      return { ...op, author }; // inner ops keep their authors
    default:
      return { ...op, author };
  }
}

/**
 * Responder election for late-join snapshots (WB-006): when a `snapshot_request`
 * arrives, only ONE peer should answer or the late joiner gets N duplicate snapshots.
 * The elected responder is the lowest-userID connected participant OTHER than the
 * requester (the same lowest-userID idiom mesh uses for offer glare). Returns true iff
 * I should respond.
 */
export function electSnapshotResponder(
  myUserID: string,
  peerUserIDs: string[],
  requesterID: string,
): boolean {
  if (myUserID === requesterID) return false; // never answer my own request
  const candidates = [myUserID, ...peerUserIDs].filter((u) => u !== requesterID);
  if (candidates.length === 0) return false;
  let lowest = candidates[0];
  for (const u of candidates) if (u < lowest) lowest = u;
  return lowest === myUserID;
}

/**
 * Tracks seen op ids so applying the same op twice (possible once mesh/SFU relay,
 * or when a snapshot overlaps in-flight live ops) is a no-op. Bounded so a long
 * session doesn't grow without limit — the oldest ids age out (ops are additive
 * and a re-delivery of a very old op is harmless if it slips through).
 */
export class OpDedup {
  private readonly seenSet = new Set<string>();
  private readonly order: string[] = [];

  constructor(private readonly max = 4096) {}

  /** Returns true the FIRST time an id is seen, false on every repeat. */
  first(id: string): boolean {
    if (this.seenSet.has(id)) return false;
    this.seenSet.add(id);
    this.order.push(id);
    if (this.order.length > this.max) {
      const evicted = this.order.shift();
      if (evicted !== undefined) this.seenSet.delete(evicted);
    }
    return true;
  }

  /** Forget everything (e.g. on reconnect before re-requesting a snapshot). */
  reset(): void {
    this.seenSet.clear();
    this.order.length = 0;
  }
}
